import React from "react";
import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { getWorkOrderStatusLog } from "../../../fetchers/cmmsWorkOrder";
import messages from "../messages";
import { formatTimestamp } from "../../cmms/cmmsFormat";
import { CmmsListSkeleton, CmmsErrorState } from "../../cmms/components/CmmsStates";
import { getAssetHierarchyPalette } from "../../cmms/assetHierarchyStyles";
import { getStatusBadge } from "../workOrderEnums";
import { getHandoverShape } from "../workOrderHandover";

/*
 * What has happened to this work order, oldest first.
 *
 * It exists because escalation moves the one order between departments instead of splitting it
 * into a parent and a child. That is what keeps the comments, attachments, hours and materials
 * attached to the fault — but it also means the order itself only ever shows the department that
 * has it *now*. Without this tab there is nowhere to see that Mekanik had it first, and the
 * receiving technician has no way to tell which of the hours above the hand-over were somebody
 * else's work.
 *
 * A row is a hand-over when it names departments or trades; workOrderHandover.js has the rule,
 * and which of the two leads the row. Since AB#1624 an order can also move between trades inside
 * one department, which the department pair alone would show as going nowhere.
 */

export default function WorkOrderHistoryList({ workOrderId, companyKey }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();

  const query = useQuery(
    ["cmmsWorkOrderStatusLog", companyKey, workOrderId],
    () => getWorkOrderStatusLog(companyKey, workOrderId),
    { enabled: Boolean(companyKey && workOrderId), staleTime: 15000 },
  );

  // A department that has since been deleted keeps its id on the row but loses its name, and an
  // order that had no department at all before the hand-over has neither. Both are said out loud
  // rather than rendered as a blank, which would read as "escalated from nowhere".
  const departmentLabel = (id, name) => {
    if (name) return name;
    if (id != null) return intl.formatMessage(messages.historyUnknownDepartment, { id });
    return intl.formatMessage(messages.historyNoDepartment);
  };

  // Trades are never deleted, only retired, so a trade id always comes with its name. The id is the
  // fallback all the same, as on the details panel, rather than a blank.
  const tradeLabel = (id, name) => {
    if (name) return name;
    if (id != null) return `#${id}`;
    return intl.formatMessage(messages.historyNoTrade);
  };

  if (query.isLoading) {
    return <CmmsListSkeleton rows={4} height={28} />;
  }

  if (query.error) {
    return (
      <CmmsErrorState
        error={query.error}
        fallback={intl.formatMessage(messages.historyLoadFailed)}
        onRetry={() => query.refetch()}
        isRetrying={query.isFetching}
      />
    );
  }

  const entries = query.data ?? [];

  if (entries.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: cmmsColors.mutedText, p: 1 }}>
        {intl.formatMessage(messages.historyEmpty)}
      </Typography>
    );
  }

  return (
    <Stack spacing={1.25} sx={{ p: 0.5 }}>
      {entries.map((entry) => {
        const handover = getHandoverShape(entry);
        const departments = handover && {
          from: departmentLabel(entry.fromDepartmentId, entry.fromDepartmentName),
          to: departmentLabel(entry.toDepartmentId, entry.toDepartmentName),
        };
        const trades = handover && {
          from: tradeLabel(entry.fromTradeId, entry.fromTradeName),
          to: tradeLabel(entry.toTradeId, entry.toTradeName),
        };
        const fromBadge = getStatusBadge(entry.fromStatus, intl);
        const toBadge = getStatusBadge(entry.toStatus, intl);

        return (
          <Box
            key={entry.id}
            sx={{
              p: 1.25,
              borderRadius: 1,
              border: `1px solid ${cmmsColors.border}`,
              // A hand-over is the row somebody opened this tab to find, so it is the one row
              // with an accent down its edge. Everything else is an ordinary status change.
              borderLeft: handover ? `4px solid #f57c00` : `1px solid ${cmmsColors.border}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
              {handover && <TrendingUpIcon fontSize="small" sx={{ color: "#f57c00" }} />}

              {handover ? (
                <Typography variant="body2" sx={{ color: cmmsColors.text, fontWeight: 700 }}>
                  {intl.formatMessage(messages.historyEscalated, handover.leadsWith === "trade" ? trades : departments)}
                </Typography>
              ) : (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Chip size="small" label={fromBadge.label} sx={{ backgroundColor: fromBadge.bg, color: fromBadge.text, fontWeight: 700 }} />
                  <Typography variant="body2" sx={{ color: cmmsColors.mutedText }}>→</Typography>
                  <Chip size="small" label={toBadge.label} sx={{ backgroundColor: toBadge.bg, color: toBadge.text, fontWeight: 700 }} />
                </Stack>
              )}
            </Stack>

            {handover?.showTradeLine && (
              <Typography variant="body2" sx={{ color: cmmsColors.text, mt: 0.5 }}>
                {intl.formatMessage(messages.historyTradeChanged, trades)}
              </Typography>
            )}

            <Typography variant="caption" sx={{ color: cmmsColors.mutedText, display: "block", mt: 0.5 }}>
              {formatTimestamp(intl, entry.changedAt)}
              {entry.userName ? ` · ${intl.formatMessage(messages.historyBy, { name: entry.userName })}` : ""}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
}
